import { FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Grid, Typography, CircularProgress } from '@mui/material'
import format from 'date-fns/format'
import PodcastsIcon from '@mui/icons-material/Podcasts'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DownloadIcon from '@mui/icons-material/Download'
import TimelineIcon from '@mui/icons-material/Timeline'
import HistoryIcon from '@mui/icons-material/History'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import SellIcon from '@mui/icons-material/Sell'
import SettingsIcon from '@mui/icons-material/Settings'
import EditIcon from '@mui/icons-material/Edit'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import BluetoothIcon from '@mui/icons-material/Bluetooth'
import BluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled'
import {
  HasSidebarLayout,
  DataLoad,
  CollapsibleSection,
  EntityHeader,
  SensorInfo,
  PropsInjector,
  TabGroupFilter,
  ProjectsCurrentProject,
  SensorChartTabs,
  SensorLastReadings,
  SensorCurrentConfig,
  SensorConfigurationInfo,
  SensorConfigurationHistory,
  SensorPastProjects,
  SensorHistoricalDataModal,
  SensorFirmwareHistory,
  SensorGpxDataModal,
  DownloadMeasurementsModal,
  SensorStagingLabel,
  StatefulDialog,
  AssignSensorToContractor,
  ManageSensorBleModal,
  SensorBackupUplinks,
  OuterLink,
  SensorArchivedLabel,
  MakeAvailableSensorModal,
  SensorRelatedRapidRhProbes,
  MarkLostSensorModal,
  DisconnectSensorModal,
  ReassignInUseSensorModal,
  SensorBatteryStats,
  SensorAlerts,
  SensorQualityControl,
  SensorQualityControlDialog,
  SensorMovementDetection,
  ActivateSensorInThingspaceModal,
  ActionsMenu,
  ActionsMenuItem
} from 'src/components'
import {
  useActivateSensorRequest,
  useSensorActivationStatusRequest,
  useAssignSensorRequest,
  useEditSensorConfigurationRequest,
  useSensorConfiguration,
  useSensorDisconnecting,
  useSensorFirmware,
  useSensorRequest,
  useSensorRetrieveFirmwareRequest,
  useSensorStatesRequest
} from 'src/hooks/api'
import { useAddPopupMessage, useDialog, useFillMissedUplinks, useQRCode, useSensorEditConfiguration, useSensorLabel, useSensorStateUpdate } from 'src/hooks/ui'
import { Sensor, SensorLastSentConfiguration, SensorState } from 'src/api'
import { DialogNames, SearchParamsKeys, SensorStateList } from 'src/enums'
import { useSensorConfiguring } from 'src/hooks/api/useSensorConfiguring'
import { capitalizeFirstLetter } from 'src/utils/formatters'
import { BleManageState } from 'src/interfaces'
import { SensorEvents } from 'src/components/sections/sensor/sensorEvents'

interface SensorShowContentProps {
  sensor?: Sensor
  refetchSensor?: () => void
  sensorStates?: SensorState[]
}
interface SensorStatusProps {
  sensor: Sensor
  sensorStates: SensorState[]
  updateState: (payload: { uniqueId: string; state: SensorState; onSuccess?: () => void; onError?: () => void }) => void
  startAssignFlow: () => void
  openDisconnectSensorDialog: () => void
  startReassignFlow: () => void
  refetchSensor?: () => void
  openSensorMakeAvailableDialog: () => void
  openSensorMarkLostDialog: () => void
}

const SensorStatus: FC<SensorStatusProps> = ({
  sensor,
  sensorStates,
  updateState,
  startAssignFlow,
  openDisconnectSensorDialog,
  startReassignFlow,
  refetchSensor,
  openSensorMakeAvailableDialog,
  openSensorMarkLostDialog
}) => {
  const getNextAction: { action: () => void } = useCallback(() => {
    const currentState: string = sensor?.state?.name || ''
    let _nextAction: () => void = () => {}
    switch (currentState) {
      case 'new':
        const nextState = sensorStates.find((state) => state.name === 'available')
        _nextAction = () =>
          updateState({
            uniqueId: sensor.uniqueId as string,
            state: nextState as SensorState,
            onSuccess: refetchSensor
          })
        break
      case 'available':
      case 'assigned':
        _nextAction = startAssignFlow
        break
      case 'in-use':
        _nextAction = openDisconnectSensorDialog
        break
      default:
        break
    }
    return {
      action: _nextAction
    }
  }, [sensor, sensorStates, updateState, startAssignFlow, openDisconnectSensorDialog, refetchSensor])()

  const updateStateButton = () => {
    const labelMap: Record<string, string> = {
      new: 'Make Ready to use',
      available: 'Assign',
      assigned: 'Reassign',
      'in-use': 'Disconnect'
    }
    const actionLabel: string = labelMap[sensor?.state?.name || '']

    return actionLabel ? `(${actionLabel})` : ''
  }

  const isInUse = sensor?.state?.name === SensorStateList.InUse
  const isLost = sensor?.state?.name === SensorStateList.Lost

  return (
    <Typography variant="h6">
      {capitalizeFirstLetter(sensor.state?.name || '')}{' '}
      {updateStateButton() ? (
        <Button onClick={getNextAction.action} variant="text">
          {updateStateButton()}
        </Button>
      ) : null}
      {isInUse ? (
        <Button onClick={startReassignFlow} variant="text">
          (Reassign)
        </Button>
      ) : null}
      <Button variant="text" onClick={isLost ? openSensorMakeAvailableDialog : openSensorMarkLostDialog}>
        ({isLost ? 'Make available' : 'Mark as lost'})
      </Button>
    </Typography>
  )
}

const getStatusList = ({ projectId, projectSectionId, uniqueId }: { projectId: number | null; projectSectionId: number | null; uniqueId: string }) => {
  return [
    {
      status: 'Current Project',
      content: <ProjectsCurrentProject projectId={projectId} projectSectionId={projectSectionId} noResultText="Current sensor no belongs to any project" showFooter={false} />
    },
    {
      status: 'Past Projects',
      content: <SensorPastProjects sensorUniqueId={uniqueId} noResultText="Current sensor does not have past projects" showFooter={false} />
    }
  ]
}

const SensorShowContent: FC<SensorShowContentProps> = ({ sensor = null, refetchSensor, sensorStates }) => {
  const [sensorBleState, setSensorBleState] = useState<BleManageState>(BleManageState.OFF)
  const { loading: loadingQRCode, generateCode } = useQRCode()
  const { generatingLabel, downloadLabel } = useSensorLabel()
  const navigate = useNavigate()
  const addPopupMessage = useAddPopupMessage()
  const { makeSensorEditConfiguration, makeNullableEmptyValues } = useSensorEditConfiguration()
  const sensorDisconnectingRequest = useSensorDisconnecting({
    uniqueId: sensor?.uniqueId || '',
    options: {
      onSuccess: () => {
        addPopupMessage({ text: 'The sensor was successfully disconnected from the project', type: 'success' })
        closeDisconnectSensorDialog()
        if (typeof refetchSensor === 'function') refetchSensor()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    }
  })
  const { openDialog: openAssignSensorDialog, closeDialog: closeAssignSensorDialog } = useDialog(DialogNames.AssignSensorToContractor)
  const { openDialog: openSensorBleDialog, closeDialog: closeSensorBleDialog } = useDialog(DialogNames.ManageSensorBle)
  const { openDialog: openDisconnectSensorDialog, closeDialog: closeDisconnectSensorDialog } = useDialog(DialogNames.DisconnectSensor)
  const { openDialog: openReassignSensorDialog, closeDialog: closeReassignSensorDialog } = useDialog(DialogNames.ReassignSensorToContractor)
  const { openDialog: openReassignConfirmDialog, closeDialog: closeReassignConfirmDialog } = useDialog(DialogNames.ReassignInUseSensor)
  const { openDialog: openAssignQualityControlDialog, closeDialog: closeAssignQualityControlDialog } = useDialog(DialogNames.AssignSensorsQualityControl)
  const { openDialog: openActivateSensorDialog, closeDialog: closeActivateSensorDialog } = useDialog(DialogNames.ActivateSensorInThingspace)
  const { openDialog: openDownloadMeasurementsDialog } = useDialog(DialogNames.DownloadMeasurements)
  const { openDialog: openSensorGpxDataDialog } = useDialog(DialogNames.SensorGpxData)
  const { openDialog: openSensorHistoricalDataDialog } = useDialog(DialogNames.GetSensorHistoricalData)
  const activateSensorRequest = useActivateSensorRequest({
    uniqueId: sensor?.uniqueId || '',
    options: {
      onSuccess: () => {
        addPopupMessage({ text: 'The device activation request was successfully sent', type: 'success' })
        closeActivateSensorDialog()
        if (typeof refetchSensor === 'function') refetchSensor()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    }
  })
  const activationStatusRequest = useSensorActivationStatusRequest({
    uniqueId: sensor?.uniqueId || '',
    options: {
      onSuccess: ({ isActivated }) => {
        addPopupMessage({ text: isActivated ? 'The GAT is active' : 'The GAT is not active', type: isActivated ? 'success' : 'warning' })
        if (typeof refetchSensor === 'function') refetchSensor()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    }
  })
  const pendingReassignContractorId = useRef<number | null>(null)
  // Assign and Reassign share one quality control gate; this records which picker to open after it.
  const pendingAssignFlow = useRef<'assign' | 'reassign'>('assign')
  const editSensorConfigurationRequest = useEditSensorConfigurationRequest({
    uniqueId: sensor?.uniqueId || '',
    options: {
      onSuccess: () => {
        addPopupMessage({ text: 'The sensor BLE configuration was successfully sent', type: 'success' })
        closeSensorBleDialog()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    }
  })
  const assignSensorRequest = useAssignSensorRequest({
    options: {
      onSettled: () => closeAssignSensorDialog(),
      onSuccess: refetchSensor
    }
  })
  const reassignSensorRequest = useAssignSensorRequest({
    options: {
      onSuccess: () => {
        addPopupMessage({ text: 'The sensor was successfully reassigned', type: 'success' })
        closeReassignConfirmDialog()
        if (typeof refetchSensor === 'function') refetchSensor()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    }
  })
  const { fillMissedUplinks, isLoading: isLoadingFillMissedUplinksRequest } = useFillMissedUplinks()
  const { isInitialLoading: isLoadingFirmwareVersion, refetch: refetchFirmwareVersion } = useSensorFirmware({
    uniqueId: sensor?.uniqueId || '',
    options: {
      enabled: false,
      retry: false
    }
  })
  const { isInitialLoading: isLoadingSensorConfiguration, refetch: refetchSensorConfiguration } = useSensorConfiguration({
    uniqueId: sensor?.uniqueId || '',
    options: {
      enabled: false,
      retry: false
    }
  })
  const sensorConfiguringRequest = useSensorConfiguring()
  const sensorRetrieveFirmwareRequest = useSensorRetrieveFirmwareRequest()
  const { openSensorMakeAvailableDialog, closeSensorMakeAvailableDialog, openSensorMarkLostDialog, closeSensorMarkLostDialog, updateSensorState, isLoadingChangingSensorState } =
    useSensorStateUpdate()
  const newState = useMemo(() => (sensorStates || []).find((state) => state.name === SensorStateList.New), [sensorStates])
  const lostState = useMemo(() => (sensorStates || []).find((state) => state.name === SensorStateList.Lost), [sensorStates])

  if (!sensor) return null

  const { uniqueId, deviceId, sensorModel, projectSectionsSensors } = sensor
  const projectId = projectSectionsSensors && projectSectionsSensors.length ? projectSectionsSensors[0].projectId : null
  const projectSectionId = projectSectionsSensors && projectSectionsSensors.length ? projectSectionsSensors[0].projectSectionId : null
  const projectName = projectSectionsSensors && projectSectionsSensors.length ? projectSectionsSensors[0].project?.name : null

  const getSensorFirmware = async () => {
    const { isSuccess, isError, error } = await refetchFirmwareVersion()
    if (isSuccess) {
      addPopupMessage({ text: 'The sensor firmware version was successfully received', type: 'success' })
      if (typeof refetchSensor === 'function') refetchSensor()
    } else if (isError) {
      addPopupMessage({ text: error?.message, type: 'error' })
    }
  }

  const configureSensor = async (sensorUniqueId: string) => {
    sensorConfiguringRequest.mutate(sensorUniqueId, {
      onSuccess: (data) => {
        addPopupMessage({ text: 'The sensor was succesfully configured', type: 'success' })
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    })
  }

  const retrieveSensorFirmware = () => {
    sensorRetrieveFirmwareRequest.mutate(uniqueId, {
      onSuccess: (data) => {
        addPopupMessage({ text: 'The sensor firmware version requested', type: 'success' })
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: 'error' })
      }
    })
  }

  const getSensorConfiguration = async () => {
    const { isSuccess, isError, error } = await refetchSensorConfiguration()
    if (isSuccess) {
      addPopupMessage({ text: 'The sensor configuration was successfully received', type: 'success' })
      if (typeof refetchSensor === 'function') refetchSensor()
    } else if (isError) {
      addPopupMessage({ text: error?.message, type: 'error' })
    }
  }

  const onFillMissedUplinks = (date: string) => {
    fillMissedUplinks(date, sensor.uniqueId)
  }

  const assignSensor = (contractorId: number | null) => {
    assignSensorRequest.mutate({
      uniqueId: sensor.uniqueId,
      payload: { contractorId }
    })
  }

  const onReassignContractorSelected = (contractorId: number | null) => {
    closeReassignSensorDialog()
    pendingReassignContractorId.current = contractorId
    openReassignConfirmDialog()
  }

  const onReassignConfirmed = () => {
    reassignSensorRequest.mutate({
      uniqueId: sensor.uniqueId,
      payload: { contractorId: pendingReassignContractorId.current }
    })
  }

  // Both the assign and the reassign flow start with the quality control report - from there the
  // admin either cancels or continues to the matching contractor picker.
  const startAssignFlow = () => {
    pendingAssignFlow.current = 'assign'
    openAssignQualityControlDialog()
  }

  const startReassignFlow = () => {
    pendingAssignFlow.current = 'reassign'
    openAssignQualityControlDialog()
  }

  const onAssignQualityControlConfirmed = () => {
    closeAssignQualityControlDialog(pendingAssignFlow.current === 'reassign' ? openReassignSensorDialog : openAssignSensorDialog)
  }

  const enableBle = () => {
    if (!sensor.lastSentConfig) {
      addPopupMessage({ text: 'The sensor configuration is not available', type: 'error' })
      return
    }

    setSensorBleState(BleManageState.ON)
    openSensorBleDialog()
  }

  const disableBle = () => {
    if (!sensor.lastSentConfig) {
      addPopupMessage({ text: 'The sensor configuration is not available', type: 'error' })
      return
    }

    setSensorBleState(BleManageState.OFF)
    openSensorBleDialog()
  }

  const manageSensorBle = (state: BleManageState) => {
    const sensorConfiguration = makeSensorEditConfiguration(sensor.lastSentConfig as SensorLastSentConfiguration)
    const data = makeNullableEmptyValues(sensorConfiguration)
    if (data.ble !== undefined) {
      data.ble.opMode = state === BleManageState.ON ? 1 : 0
      data.ble.reportType = state === BleManageState.ON ? 2 : 0
    }
    delete data.firmware
    editSensorConfigurationRequest.mutate(data)
  }

  const configurationMenuItems: ActionsMenuItem[] = [
    { label: 'Configure sensor', icon: <SettingsIcon fontSize="small" />, onClick: () => configureSensor(uniqueId), loading: sensorConfiguringRequest.isLoading },
    { label: 'Edit configuration', icon: <EditIcon fontSize="small" />, onClick: () => navigate(`/sensors/configuration/edit/${uniqueId}`) }
  ]

  const firmwareMenuItems: ActionsMenuItem[] = [
    { label: 'Request version from sensor', icon: <PodcastsIcon fontSize="small" />, onClick: retrieveSensorFirmware, loading: sensorRetrieveFirmwareRequest.isLoading },
    { label: 'Get version from ThingSpace', icon: <CloudDownloadIcon fontSize="small" />, onClick: getSensorFirmware, loading: isLoadingFirmwareVersion }
  ]

  const dataMenuItems: ActionsMenuItem[] = [
    { label: 'Download measurements', icon: <DownloadIcon fontSize="small" />, onClick: () => openDownloadMeasurementsDialog() },
    { label: 'Download GPX track', icon: <TimelineIcon fontSize="small" />, onClick: () => openSensorGpxDataDialog() },
    { label: 'Get historical data', icon: <HistoryIcon fontSize="small" />, onClick: () => openSensorHistoricalDataDialog() }
  ]

  const labelsMenuItems: ActionsMenuItem[] = [
    { label: 'Generate QR code', icon: <QrCode2Icon fontSize="small" />, onClick: () => generateCode(deviceId || uniqueId), loading: loadingQRCode },
    { label: 'Download label', icon: <SellIcon fontSize="small" />, onClick: () => downloadLabel(deviceId as string), loading: generatingLabel }
  ]

  const thingSpaceMenuItems: ActionsMenuItem[] = [
    // { label: 'Request sensor activation', icon: <PowerSettingsNewIcon fontSize="small" />, onClick: () => openActivateSensorDialog(), disabled: !isSensorDeactivated(sensor) },
    { label: 'Check activation status', icon: <FactCheckIcon fontSize="small" />, onClick: () => activationStatusRequest.mutate(), loading: activationStatusRequest.isLoading }
  ]

  const bleMenuItems: ActionsMenuItem[] = [
    { label: 'Turn on BLE', icon: <BluetoothIcon fontSize="small" />, onClick: enableBle },
    { label: 'Turn off BLE', icon: <BluetoothDisabledIcon fontSize="small" />, onClick: disableBle }
  ]

  const getRightIcon = (): React.ReactNode => {
    const nodes: React.ReactNode[] = []
    if (sensor.isStagingSensor) {
      nodes.push(<SensorStagingLabel size="medium" key="staging-label" sx={{ mr: 1 }} />)
    }
    if (sensor.state?.name === SensorStateList.Archived) {
      nodes.push(<SensorArchivedLabel size="medium" key="archived-lable" />)
    }
    return nodes
  }

  const title = `${sensorModel?.displayName || 'Sensor'}: ${uniqueId}`
  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <EntityHeader
            title={title}
            rightIcon={getRightIcon()}
            actionBlockOnTheRight={
              <SensorStatus
                sensor={sensor}
                sensorStates={sensorStates || []}
                updateState={updateSensorState}
                startAssignFlow={startAssignFlow}
                openDisconnectSensorDialog={openDisconnectSensorDialog}
                startReassignFlow={startReassignFlow}
                refetchSensor={refetchSensor}
                openSensorMakeAvailableDialog={openSensorMakeAvailableDialog}
                openSensorMarkLostDialog={openSensorMarkLostDialog}
              />
            }
          >
            <SensorAlerts sensor={sensor} />

            <Grid container spacing={2}>
              <Grid item>
                <ActionsMenu label="Configuration" items={configurationMenuItems} />
              </Grid>
              <Grid item>
                <ActionsMenu label="Labels" items={labelsMenuItems} />
              </Grid>
              <Grid item>
                <ActionsMenu label="Firmware" items={firmwareMenuItems} />
              </Grid>
              <Grid item>
                <ActionsMenu label="Data" items={dataMenuItems} />
              </Grid>
              <Grid item>
                <ActionsMenu label="ThingSpace" items={thingSpaceMenuItems} />
              </Grid>
              <Grid item>
                <ActionsMenu label="BLE" items={bleMenuItems} />
              </Grid>
            </Grid>
            <SensorHistoricalDataModal hideTriggerButton onFillMissedUplinks={onFillMissedUplinks} isLoading={isLoadingFillMissedUplinksRequest} />
            <SensorGpxDataModal hideTriggerButton sensorUniqueId={uniqueId} />
            <DownloadMeasurementsModal hideTriggerButton deviceType="sensor" deviceId={uniqueId} />
          </EntityHeader>
        </Grid>
        <Grid container item xs={12} flexDirection="column">
          <Grid container item alignItems="center" spacing={2} mb={1}>
            <Grid item>
              <Typography variant="h6">Sensor Information</Typography>
            </Grid>
            <Grid item>
              <Button variant="text">
                <OuterLink to={`/sensors/${uniqueId}/extended-diagnostics`}>Get Extended Info</OuterLink>
              </Button>
            </Grid>
          </Grid>
          <Grid item sx={{ width: '100%', minWidth: 0 }}>
            <SensorInfo sensor={sensor} refetchSensor={refetchSensor} />
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <SensorQualityControl sensorUniqueId={uniqueId} />
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title={`Current project: ${projectName || 'none'}`}>
            <TabGroupFilter
              name={SearchParamsKeys.Tab}
              statusList={getStatusList({ projectId, projectSectionId, uniqueId })}
              paramsToRemove={[SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]}
            />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Chart Of Readings">
            <SensorChartTabs sensor={sensor} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Movement Detection">
            <SensorMovementDetection sensorUniqueId={sensor.uniqueId} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Battery Statistics">
            <SensorBatteryStats sensorUniqueId={sensor.uniqueId} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Last Readings">
            <SensorLastReadings sensor={sensor} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Events">
            <SensorEvents sensorId={sensor.id} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Last received raw data">
            <SensorBackupUplinks sensorUniqueId={sensor.uniqueId} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Related Rapid RH Probes">
            <SensorRelatedRapidRhProbes sensorUniqueId={sensor.uniqueId} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor Current Configuration (updated automatically)">
            <SensorCurrentConfig sensor={sensor} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection
            title={`Sensor Configuration Info${sensor.currentConfig?.time ? ` (last requested on ${format(new Date(sensor.currentConfig?.time), 'PPpp')})` : ''}`}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Button variant="outlined" size="large" onClick={getSensorConfiguration} disabled={isLoadingSensorConfiguration} sx={{ width: 260 }}>
                  {isLoadingSensorConfiguration ? <CircularProgress size={26} /> : 'Request info update'}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <SensorConfigurationInfo sensor={sensor} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Sensor Firmware History
                </Typography>
                <SensorFirmwareHistory history={sensor.firmwareHistory || []} />
              </Grid>
            </Grid>
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Sensor configuration history">
            <SensorConfigurationHistory sensorUniqueId={sensor.uniqueId as string} />
          </CollapsibleSection>
        </Grid>
      </Grid>
      <StatefulDialog name={DialogNames.AssignSensorToContractor} wrapContent={false}>
        <AssignSensorToContractor onSubmit={assignSensor} />
      </StatefulDialog>
      <ManageSensorBleModal
        onSubmit={manageSensorBle}
        onClose={closeSensorBleDialog}
        bleState={sensorBleState}
        isLoading={editSensorConfigurationRequest.isLoading}
        deviceId={sensor.deviceId || ''}
      />
      <MakeAvailableSensorModal
        sensorDeviceId={sensor.deviceId as string}
        isLoading={isLoadingChangingSensorState}
        onCancel={() => closeSensorMakeAvailableDialog()}
        onSubmit={() => {
          updateSensorState({
            uniqueId: sensor.uniqueId as string,
            state: newState as SensorState,
            onSuccess: refetchSensor
          })
        }}
      />
      <MarkLostSensorModal
        sensorDeviceId={sensor.deviceId as string}
        isLoading={isLoadingChangingSensorState}
        sensorStateName={sensor.state?.name}
        contractorName={sensor.contractorSensors?.[0]?.contractor?.name}
        projectName={sensor.projectSectionsSensors?.[0]?.project?.name}
        onCancel={() => closeSensorMarkLostDialog()}
        onSubmit={() => {
          updateSensorState({
            uniqueId: sensor.uniqueId as string,
            state: lostState as SensorState,
            onSuccess: refetchSensor
          })
        }}
      />
      <DisconnectSensorModal
        projectName={sensor.projectSectionsSensors?.[0]?.project?.name as string}
        isLoading={sensorDisconnectingRequest.isLoading}
        onSubmit={() => sensorDisconnectingRequest.mutate({ projectId: sensor.projectSectionsSensors?.[0]?.projectId as number })}
        onCancel={() => closeDisconnectSensorDialog()}
      />
      <StatefulDialog name={DialogNames.ReassignSensorToContractor} wrapContent={false}>
        <AssignSensorToContractor onSubmit={onReassignContractorSelected} dialogName={DialogNames.ReassignSensorToContractor} />
      </StatefulDialog>
      <ReassignInUseSensorModal
        projectName={sensor.projectSectionsSensors?.[0]?.project?.name as string}
        isLoading={reassignSensorRequest.isLoading}
        onSubmit={onReassignConfirmed}
        onCancel={() => closeReassignConfirmDialog()}
      />
      <SensorQualityControlDialog
        name={DialogNames.AssignSensorsQualityControl}
        uniqueIds={[uniqueId]}
        title="Quality Control - review before assigning"
        confirmLabel="Continue"
        onConfirm={onAssignQualityControlConfirmed}
        onClose={() => closeAssignQualityControlDialog()}
      />
      <ActivateSensorInThingspaceModal
        imei={sensor.imei || sensor.thingspaceSensor?.imei || ''}
        isLoading={activateSensorRequest.isLoading}
        onSubmit={(servicePlan) => activateSensorRequest.mutate({ servicePlan })}
        onCancel={() => closeActivateSensorDialog()}
      />
    </>
  )
}

const SensorDataLoader: FC<PropsWithChildren<{ sensorUniqueId: string }>> = ({ sensorUniqueId, children }) => {
  const { isInitialLoading, isError, data, error, refetch } = useSensorRequest({
    uniqueId: sensorUniqueId,
    options: {
      notifyOnChangeProps: ['isLoading', 'isError', 'isRefetching']
    }
  })
  document.title = `CLīMIT Admin Panel - Sensor ${data?.deviceId || ''}`
  const { data: sensorStateData } = useSensorStatesRequest()

  const props = {
    sensor: data || null,
    refetchSensor: refetch,
    sensorStates: sensorStateData || []
  }

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

const SensorShow: FC = () => {
  const params = useParams()
  const { uniqueId } = params as { uniqueId: string }

  return (
    <HasSidebarLayout>
      <SensorDataLoader sensorUniqueId={uniqueId}>
        <SensorShowContent />
      </SensorDataLoader>
    </HasSidebarLayout>
  )
}

export default SensorShow
